import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { api } from "../lib/api-client.js";

type Request = <T>(path: string, init?: RequestInit) => Promise<T>;
interface ReadOptions { signal?: AbortSignal; force?: boolean; }

function freshness(path: string) {
  return /^\/(teams|members|config-libraries)(?:[/?]|$)/.test(path) ? 30_000 : 10_000;
}

/** One product and verified authorization scope; never persisted to browser storage. */
export function createPageResourceClient(client: QueryClient, scope: string, request: Request = api) {
  const prefix = ["vla-page-resources", scope] as const;
  let disposed = false;
  const pending = new Map<AbortController, string>();
  const retireRead = (path: string) => {
    // Query cancellation is synchronous; retire its result before aborting transport.
    void client.cancelQueries({ queryKey: [...prefix, path], exact: true });
    pending.forEach((resourcePath, controller) => {
      if (resourcePath === path) { controller.abort(); pending.delete(controller); }
    });
  };
  // Observer teardown must not abort a breadcrumb or prefetch sharing this read.
  // The authorization scope owns transport cancellation; Query owns subscriptions.
  const options = <T,>(path: string) => queryOptions({
    queryKey: [...prefix, path], staleTime: freshness(path), gcTime: 5 * 60_000,
    retry: false, refetchOnWindowFocus: false,
    queryFn: async () => {
      if (disposed) throw new DOMException("Resource scope has ended", "AbortError");
      const controller = new AbortController();
      pending.set(controller, path);
      try {
        const result = await request<T>(path, { signal: controller.signal });
        controller.signal.throwIfAborted();
        return result;
      } finally { pending.delete(controller); }
    },
  });
  return {
    client,
    options,
    update<T>(path: string, value: T | ((previous: T | undefined) => T | undefined)) {
      if (disposed) return;
      retireRead(path);
      client.setQueryData<T>([...prefix, path], value);
    },
    /** Only confirmed, fresh data from this live authorization scope can seed a page. */
    peek<T>(path: string): T | undefined {
      if (disposed) return undefined;
      const state = client.getQueryState<T>([...prefix, path]);
      if (!state || state.status !== "success" || state.isInvalidated || Date.now() - state.dataUpdatedAt >= freshness(path)) return undefined;
      return state.data;
    },
    async read<T>(path: string, { signal, force = false }: ReadOptions = {}): Promise<T> {
      signal?.throwIfAborted();
      if (disposed) throw new DOMException("Resource scope has ended", "AbortError");
      const queryKey = [...prefix, path];
      if (force) {
        retireRead(path);
        await client.invalidateQueries({ queryKey, exact: true, refetchType: "none" });
      }
      signal?.throwIfAborted();
      if (disposed) throw new DOMException("Resource scope has ended", "AbortError");
      const result = client.fetchQuery(options<T>(path));
      if (!signal) return result;
      // A departing consumer stops waiting; other consumers retain their shared request.
      return new Promise<T>((resolve, reject) => {
        const abort = () => reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
        signal.addEventListener("abort", abort, { once: true });
        if (signal.aborted) abort();
        void result.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
      });
    },
    invalidate() {
      return client.invalidateQueries({ queryKey: prefix, refetchType: "none" });
    },
    dispose() {
      disposed = true;
      pending.forEach((_path, controller) => controller.abort());
      pending.clear();
      void client.cancelQueries({ queryKey: prefix });
      client.removeQueries({ queryKey: prefix });
    },
  };
}

export type PageResourceClient = ReturnType<typeof createPageResourceClient>;
